import { SvgIcons } from './svg-icons';
import { imgs } from './svg-icons/imgs';

interface IngredientIconProps {
  /** The ingredient object with nameAr, nameFr, and optional emoji/iconId */
  ingredient: {
    nameAr: string;
    nameFr: string;
    emoji?: string;
  } | null;
  /** Size of the icon in pixels (default: 64) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Maps ingredient names (in French or Arabic) to icon IDs
 */
const INGREDIENT_ICON_MAP: Record<string, string> = {
  // Vegetables - French
  tomate: 'tomato',
  tomates: 'tomato',
  carotte: 'carrot',
  carottes: 'carrot',
  oignon: 'onion',
  oignons: 'onion',
  ail: 'garlic',
  poivrons: 'green-pepper',
  'poivron jaune': 'yellow-pepper',
  'poivron rouge': 'red-pepper',
  'oignon rouge': 'red-onion',
  concombre: 'cucumber',
  courgette: 'cucumber',
  aubergine: 'eggplant',
  brocoli: 'broccoli',
  chou: 'cabbage',
  'chou-fleur': 'cabbage',
  'pomme de terre': 'potato',
  'pommes de terre': 'potato',
  patate: 'potato',
  champignon: 'mushroom',
  champignons: 'mushroom',
  maïs: 'corn',
  avocat: 'avocado',
  laitue: 'salad',

  // Vegetables - Arabic
  طماطم: 'tomato',
  بندورة: 'tomato',
  جزر: 'carrot',
  بصل: 'onion',
  ثوم: 'garlic',
  فلفل: 'green-pepper',
  'haricots rouges' : 'red-beans',
  'فلفل أحمر': 'red-pepper',
  'فلفل أصفر': 'yellow-pepper',
  'بصل أحمر': 'red-onion',
  خيار: 'cucumber',
  كوسة: 'cucumber',
  باذنجان: 'eggplant',
  بروكلي: 'broccoli',
  ملفوف: 'cabbage',
  بطاطس: 'potato',
  فطر: 'mushroom',
  'فلفل حار': 'chili-pepper',
  ذرة: 'corn',
  افوكادو: 'avocado',

  // Fruits - French
  pomme: 'apple',
  pommes: 'apple',
  orange: 'orange',
  oranges: 'orange',
  citron: 'citrus',
  banane: 'bananas',
  bananes: 'bananas',
  fraise: 'strawberry',
  fraises: 'strawberry',
  raisin: 'grapes',
  raisins: 'grapes',
  ananas: 'pineapple',
  poire: 'pear',
  poires: 'pear',
  mangue: 'mango',
  pêche: 'peach',
  pêches: 'peach',
  pastèque: 'watermelon',
  kiwi: 'kiwi',

  // Fruits - Arabic
  تفاح: 'apple',
  برتقال: 'orange',
  ليمون: 'citrus',
  موز: 'bananas',
  فراولة: 'strawberry',
  عنب: 'grapes',
  أناناس: 'pineapple',
  إجاص: 'pear',
  كمثرى: 'pear',
  مانجو: 'mango',
  خوخ: 'peach',
  بطيخ: 'watermelon',
  كيوي: 'kiwi',

  // Meat & Protein - French
  poulet: 'chicken',
  viande: 'chicken-leg',
  dinde: 'turkey',
  poisson: 'fish',
  crevette: 'shrimp',
  crevettes: 'shrimp',
  'fruits de mer': 'seafood',

  // Meat & Protein - Arabic
  دجاج: 'chicken',
  لحم: 'chicken-leg',
  'ديك رومي': 'turkey',
  سمك: 'fish',
  جمبري: 'shrimp',
  روبيان: 'shrimp',

  // Dairy - French
  lait: 'milk',
  fromage: 'cheese',
  yaourt: 'yogurt',

  // Dairy - Arabic
  حليب: 'milk',
  جبن: 'cheese',
  زبادي: 'yogurt',
  لبن: 'yogurt',

  // Grains & Pasta - French
  pain: 'bread',
  croissant: 'croissant',
  pâtes: 'pasta',
  spaghetti: 'spaghetti',
  riz: 'biryani',

  // Grains & Pasta - Arabic
  خبز: 'bread',
  كرواسون: 'croissant',
  معكرونة: 'pasta',
  سباغيتي: 'spaghetti',
  أرز: 'biryani',

  // Oils & Condiments - French
  huile: 'vegetable-oil',
  "huile d'olive": 'olive-oil',
  poivre: 'pepper',

  // Oils & Condiments - Arabic
  زيت: 'vegetable-oil',
  'زيت زيتون': 'olive-oil',
  'فلفل أسود': 'pepper',

  // Beverages - French
  jus: 'orange-juice',
  "jus d'orange": 'orange-juice',
  café: 'expresso',
  smoothie: 'smoothie',

  // Beverages - Arabic
  عصير: 'orange-juice',
  'عصير برتقال': 'orange-juice',
  قهوة: 'expresso',

  // Prepared Foods - French
  pizza: 'pizza',
  burger: 'burger',
  hamburger: 'hamburger',
  frites: 'french-fries',
  salade: 'salad',
  soupe: 'hot-pot',
  gâteau: 'cake',
  cookie: 'cookies',
  cookies: 'cookies',
  donut: 'donut',

  // Prepared Foods - Arabic
  بيتزا: 'pizza',
  برجر: 'burger',
  'بطاطس مقلية': 'french-fries',
  سلطة: 'salad',
  حساء: 'hot-pot',
  كعكة: 'cake',
};

/**
 * Get icon ID for an ingredient based on its name or stored icon ID
 */
function getIconId(ingredient: { nameAr: string; nameFr: string; emoji?: string } | null): string {
  if (!ingredient) return 'dish';

  // Priority 1: Check if emoji field contains an icon ID (SVG or beverage image)
  if (ingredient.emoji) {
    // Check if it's a beverage image
    if (imgs.beverages[ingredient.emoji as keyof typeof imgs.beverages]) {
      return ingredient.emoji;
    }
    // Check if it's an SVG icon
    if (SvgIcons[ingredient.emoji]) {
      return ingredient.emoji;
    }
  }

  // Priority 2: Try French name (lowercase)
  const frenchLower = ingredient.nameFr?.toLowerCase().trim();
  if (frenchLower && INGREDIENT_ICON_MAP[frenchLower]) {
    return INGREDIENT_ICON_MAP[frenchLower];
  }

  // Priority 3: Try Arabic name
  const arabicTrimmed = ingredient.nameAr?.trim();
  if (arabicTrimmed && INGREDIENT_ICON_MAP[arabicTrimmed]) {
    return INGREDIENT_ICON_MAP[arabicTrimmed];
  }

  // Priority 4: Try partial match for French
  if (frenchLower) {
    for (const [key, iconId] of Object.entries(INGREDIENT_ICON_MAP)) {
      if (frenchLower.includes(key) || key.includes(frenchLower)) {
        return iconId;
      }
    }
  }

  return 'tomato';
}

/**
 * IngredientIcon component
 * Displays SVG icons at 64x64 size, or images for beverages
 */
export function IngredientIcon({ ingredient, size = 64, className }: IngredientIconProps) {
  const iconId = getIconId(ingredient);
  const Icon = SvgIcons[iconId] || SvgIcons['tomato'];
  const beverageImage = imgs.beverages[iconId as keyof typeof imgs.beverages];

  // If it's a beverage image, render the image
  if (beverageImage) {
    return (
      <img
        src={beverageImage}
        alt={ingredient?.nameFr || ingredient?.nameAr || 'Ingredient'}
        width={size}
        height={size}
        className={className}
        aria-label={ingredient?.nameFr || ingredient?.nameAr || 'Ingredient'}
      />
    );
  }

  // Otherwise, render the SVG icon
  return (
    Icon && (
      <Icon
        width={size}
        height={size}
        className={className}
        aria-label={ingredient?.nameFr || ingredient?.nameAr || 'Ingredient'}
      />
    )
  );
}

/**
 * Export utilities
 */
export { INGREDIENT_ICON_MAP, getIconId, SvgIcons };
